import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { useHeader } from '../context/HeaderContext';
import { usePostHog } from 'posthog-js/react';
import api from '../services/api';
import socketService from '../services/socket';
import InterventionModal from '../components/InterventionModal';
import {
    Play,
    Pause,
    SkipBack,
    SkipForward,
    StopCircle,
    Download,
    Trash2,
    Filter,
    CheckCircle,
    XCircle,
    Clock,
    Loader2,
    Sparkles,
    X
} from 'lucide-react';
import './LiveTestExecution.css';

function LiveTestExecution() {
    const { id } = useParams();

    const { currentWorkspace } = useWorkspace();
    const { setHeaderLeft } = useHeader();
    const navigate = useNavigate();
    const logsEndRef = useRef(null);
    const posthog = usePostHog();

    const [execution, setExecution] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isPaused, setIsPaused] = useState(false);
    const [currentScreenshot, setCurrentScreenshot] = useState(null);
    const [logs, setLogs] = useState([]);
    const [activeLogFilter, setActiveLogFilter] = useState('All');
    const [testScenarios, setTestScenarios] = useState([]);
    const [aiInsight, setAiInsight] = useState(null);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [executionStatus, setExecutionStatus] = useState('queued');
    const [interventionRequest, setInterventionRequest] = useState(null);
    const [zoomedImage, setZoomedImage] = useState(null);

    // Load execution data
    const loadExecution = useCallback(async () => {
        try {
            const response = await api.get(`/tests/executions/${id}`, {
                params: { workspaceId: currentWorkspace?.id }
            });
            const executionData = response.data;
            setExecution(executionData);

            // Load any already-executed steps for live tests
            if (executionData.executedSteps) {
                const steps = typeof executionData.executedSteps === 'string'
                    ? JSON.parse(executionData.executedSteps)
                    : executionData.executedSteps;
                setTestScenarios(steps);
            }
        } catch (error) {
            console.error('Failed to load execution:', error);
        } finally {
            setLoading(false);
        }
    }, [id, currentWorkspace?.id]);

    // Redirect to results page if test is completed
    useEffect(() => {
        if (execution && ['success', 'failed', 'error', 'cancelled', 'inconclusive'].includes(execution.status)) {
            navigate(`/test/results/${id}`, { replace: true });
        }
    }, [execution, id, navigate]);

    // Handle stop run
    const handleStopRun = async () => {
        try {
            await api.post(`/tests/executions/${id}/cancel`, {
                workspaceId: currentWorkspace?.id
            });
            setLogs(prev => [...prev, {
                type: 'INFO',
                message: 'Test execution stopped by user',
                timestamp: new Date().toLocaleTimeString()
            }]);
        } catch (error) {
            console.error('Failed to stop execution:', error);
            setLogs(prev => [...prev, {
                type: 'ERROR',
                message: 'Failed to stop execution: ' + error.message,
                timestamp: new Date().toLocaleTimeString()
            }]);
        }
    };

    // Handle pause/resume
    const handlePauseResume = () => {
        setIsPaused(!isPaused);
        setLogs(prev => [...prev, {
            type: 'INFO',
            message: isPaused ? 'Test execution resumed' : 'Test execution paused',
            timestamp: new Date().toLocaleTimeString()
        }]);
    };


    // Socket connection for real-time updates
    useEffect(() => {
        loadExecution();

        socketService.connect();
        if (currentWorkspace?.id) {
            socketService.joinWorkspace(currentWorkspace.id);
        }

        const handleUpdate = (data) => {
            // Convert both IDs to strings for comparison (executionId might be number or string)
            const dataExecutionId = String(data.executionId);
            const currentExecutionId = String(id);

            console.log('Socket update received:', {
                dataExecutionId,
                currentExecutionId,
                matches: dataExecutionId === currentExecutionId,
                hasScreenshotUrl: !!data.screenshotUrl,
                hasScreenshot: !!data.screenshot,
                screenshotLength: data.screenshot?.length,
                data
            });

            if (dataExecutionId === currentExecutionId) {
                // Update screenshot - prioritize URL from Picstro, fallback to base64
                if (data.screenshotUrl) {
                    // console.log('Setting screenshot from Picstro URL:', data.screenshotUrl);
                    setCurrentScreenshot(data.screenshotUrl);
                } else if (data.screenshot) {
                    // Fallback to base64 if URL not available
                    const screenshotUrl = data.screenshot.startsWith('data:')
                        ? data.screenshot
                        : `data:image/png;base64,${data.screenshot}`;

                    // console.log('Setting screenshot from base64 fallback');
                    setCurrentScreenshot(screenshotUrl);
                }

                if (data.status) {
                    setExecutionStatus(data.status);
                }

                // Handle human intervention requests
                if (data.requiresHumanIntervention && data.interventionRequest) {
                    setInterventionRequest(data.interventionRequest);
                } else if (data.status && data.status !== 'paused') {
                    // Clear request if we moved to another status
                    setInterventionRequest(null);
                }

                // Update AI insight (backend sends action + reasoning directly)
                if (data.action && data.reasoning) {
                    setAiInsight({
                        action: data.action,
                        reasoning: data.reasoning
                    });
                    setLogs(prev => [...prev, {
                        type: 'AI',
                        message: `[${data.action}] ${data.reasoning}`,
                        timestamp: data.timestamp || new Date().toLocaleTimeString()
                    }]);
                }

                // Also handle aiDecision format (for compatibility)
                if (data.aiDecision) {
                    setAiInsight({
                        action: data.aiDecision.action,
                        reasoning: data.aiDecision.reasoning
                    });
                    setLogs(prev => [...prev, {
                        type: 'AI',
                        message: `[${data.aiDecision.action}] ${data.aiDecision.reasoning}`,
                        timestamp: data.timestamp || new Date().toLocaleTimeString()
                    }]);
                }

                // Add message to logs
                if (data.message) {
                    const logType = data.status === 'error' ? 'ERROR' :
                        data.status === 'warning' ? 'WARN' : 'INFO';
                    setLogs(prev => [...prev, {
                        type: logType,
                        message: data.message,
                        timestamp: data.timestamp || new Date().toLocaleTimeString()
                    }]);
                }

                // Update test scenarios/steps from 'step' field
                if (data.step !== undefined) {

                    setTestScenarios(prev => {
                        const stepIndex = data.step - 1; // Backend sends 1-indexed
                        const newScenarios = [...prev];

                        // Ensure we have enough scenario slots
                        while (newScenarios.length <= stepIndex) {
                            newScenarios.push({
                                id: newScenarios.length,
                                name: `Step ${newScenarios.length + 1}`,
                                status: 'pending',
                                duration: '-'
                            });
                        }

                        // Update current step
                        newScenarios[stepIndex] = {
                            ...newScenarios[stepIndex],
                            name: data.action ? `${data.action}` : newScenarios[stepIndex].name,
                            status: 'running'
                        };

                        // Mark previous step as passed (if exists)
                        if (stepIndex > 0 && newScenarios[stepIndex - 1].status === 'running') {
                            newScenarios[stepIndex - 1].status = 'pass';
                        }

                        return newScenarios;
                    });
                }

                // Handle executed steps from completion
                if (data.executedSteps && Array.isArray(data.executedSteps)) {
                    setTestScenarios(data.executedSteps.map((step, index) => ({
                        id: index,
                        name: step.action || step.step?.description || `Step ${index + 1}`,
                        status: step.success ? 'pass' : 'fail',
                        duration: step.duration || '-'
                    })));
                }


                // Reload on completion
                if (['success', 'failed', 'error', 'cancelled', 'inconclusive'].includes(data.status)) {
                    // loadExecution();

                    // Show toast and redirect to results page
                    setLogs(prev => [...prev, {
                        type: 'INFO',
                        message: 'Test completed. Redirecting to results page...',
                        timestamp: new Date().toLocaleTimeString()
                    }]);

                    setTimeout(() => {
                        navigate(`/test/results/${id}`);
                    }, 2000);
                }
            }
        };

        socketService.onTestUpdate(handleUpdate);

        return () => {
            socketService.offTestUpdate(handleUpdate);
        };
    }, [id, currentWorkspace?.id, loadExecution, navigate]);

    // Timer
    useEffect(() => {
        const timer = setInterval(() => {
            if (!isPaused) {
                setElapsedTime(prev => {
                    if (execution?.startedAt) {
                        const start = new Date(execution.startedAt).getTime();
                        return Math.floor((Date.now() - start) / 1000);
                    }
                    return prev + 1;
                });
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [isPaused, execution?.startedAt]);

    // Handle user answering intervention
    const handleInterventionResponse = async (action, input = '') => {
        try {
            if (posthog) {
                posthog.capture('intervention_responded', {
                    executionId: id,
                    action,
                    hasInput: !!input
                });
            }
            await api.post(`/tests/executions/${id}/intervention`, { action, input }, {
                params: { workspaceId: currentWorkspace?.id }
            });
            setInterventionRequest(null);
            setLogs(prev => [...prev, {
                type: 'INFO',
                message: `Sent human intervention response: ${action}${input ? " (with input)" : ""}`,
                timestamp: new Date().toLocaleTimeString()
            }]);
        } catch (error) {
            console.error('Failed to submit intervention response:', error);
            setLogs(prev => [...prev, {
                type: 'ERROR',
                message: `Failed to submit intervention response: ${error.message || 'Unknown error'}`,
                timestamp: new Date().toLocaleTimeString()
            }]);
        }
    };

    const handleZoomImage = (imageUrl) => {
        if (!imageUrl) return;
        setZoomedImage(imageUrl);
    };

    const handleCloseZoom = () => {
        setZoomedImage(null);
    };

    // Auto-scroll logs
    useEffect(() => {
        logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [logs]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const filteredLogs = logs.filter(log => {
        if (activeLogFilter === 'All') return true;
        if (activeLogFilter === 'AI') return log.type === 'AI';
        if (activeLogFilter === 'Info') return log.type === 'INFO';
        if (activeLogFilter === 'Errors') return log.type === 'ERROR' || log.type === 'WARN';
        if (activeLogFilter === 'Network') return log.type === 'NETWORK';
        return true;
    });

    const getStatusIcon = (status) => {
        switch (status) {
            case 'pass': return <CheckCircle size={14} className="status-icon pass" />;
            case 'fail': return <XCircle size={14} className="status-icon fail" />;
            case 'running': return <Loader2 size={14} className="status-icon running" />;
            default: return <Clock size={14} className="status-icon pending" />;
        }
    };

    // Stats
    const passCount = testScenarios.filter(s => s.status === 'pass').length;
    const failCount = testScenarios.filter(s => s.status === 'fail').length;

    // Set header content for MainLayout
    useEffect(() => {
        const headerContent = (
            <>
                <div className="header-breadcrumb">
                    <Link to="/" className="breadcrumb-item">Dashboard</Link>
                    <span className="breadcrumb-separator">/</span>
                    <Link to="/executions" className="breadcrumb-item">Test Runs</Link>
                    <span className="breadcrumb-separator">/</span>
                    <span className="breadcrumb-current">Run #{id?.slice(-4)}</span>
                    <span className="platform-badge">{execution?.testGoal?.platform || 'Android'}</span>
                </div>
                <div className="header-status">
                    <span className={`status-indicator ${executionStatus || 'running'}`}>
                        <span className="status-dot"></span>
                        {executionStatus || 'Running'}
                    </span>
                    <span className="stat-item">✓ {passCount}</span>
                    <span className="error">✗ {failCount}</span>
                </div>

                <div className="header-actions">
                    {executionStatus === 'running' && (
                        <>
                            <button
                                className="btn btn-danger btn-sm"
                                onClick={handleStopRun}
                                disabled={executionStatus === 'completed' || executionStatus === 'failed'}
                            >
                                <StopCircle size={14} />
                                Stop Run
                            </button>
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={handlePauseResume}
                                disabled={executionStatus === 'completed' || executionStatus === 'failed'}
                            >
                                {isPaused ? <Play size={14} /> : <Pause size={14} />}
                                {isPaused ? 'Resume' : 'Pause'}
                            </button>
                        </>
                    )}
                </div>
            </>
        );

        setHeaderLeft(headerContent);

        // Cleanup: remove header content when component unmounts
        return () => setHeaderLeft(null);
    }, [id, execution, passCount, failCount, setHeaderLeft]);

    const [waitingTextIndex, setWaitingTextIndex] = useState(0);

    const waitingTexts = [
        "Setting things up...",
        "Configuring device...",
        "Waiting for test steps..."
    ];

    useEffect(() => {
        // Only run interval if we have no scenarios yet
        if (testScenarios.length > 0) return;

        const textInterval = setInterval(() => {
            setWaitingTextIndex(prev => (prev + 1) % waitingTexts.length);
        }, 10000);

        return () => clearInterval(textInterval);
    }, [testScenarios.length]);

    // Remove page-content padding for full-screen layout
    useEffect(() => {
        const pageContent = document.querySelector('.page-content');
        if (pageContent) {
            pageContent.style.padding = '0';
        }
        return () => {
            if (pageContent) {
                pageContent.style.padding = '';
            }
        };
    }, []);

    if (loading) {
        return (
            <div className="live-execution loading-state">
                <div className="spinner"></div>
                <p>Loading test execution...</p>
            </div>
        );
    }

    return (
        <div className="live-execution">
            <div className="execution-content">
                {/* Left Panel - Test Scenarios */}
                <div className="scenarios-panel">
                    {/* <div className="panel-search">
                        <Filter size={14} />
                        <input type="text" placeholder="Filter scenarios..." className="filter-input" />
                    </div> */}

                    <div className="scenarios-list">
                        {testScenarios.length > 0 ? (
                            testScenarios.map((scenario, index) => (
                                <div
                                    key={index}
                                    className={`scenario-item ${scenario.status}`}
                                >
                                    {getStatusIcon(scenario.status)}
                                    <span className="scenario-name">{scenario.name}</span>
                                    <span className="scenario-duration">{scenario.duration}</span>
                                </div>
                            ))
                        ) : (
                            <div className="scenarios-empty" style={{
                                transition: "opacity 0.5s ease-in-out"
                            }}>
                                <p key={waitingTextIndex} className="fade-in-text">{waitingTexts[waitingTextIndex]}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Center Panel - Device Viewer */}
                <div className="device-panel">
                    {execution?.metadata?.selectedDevice && (
                        <div className="auto-selected-badge">
                            Device: {execution?.metadata?.autoSelected ? '[Auto-selected]' : ''} {execution.metadata.selectedDevice.name} ({execution.metadata.selectedDevice.os})
                        </div>
                    )}

                    <div className="device-frame">
                        <div className="device-notch"></div>
                        <div className="device-screen">
                            {currentScreenshot ? (
                                <img
                                    src={currentScreenshot}
                                    alt="Device screen"
                                    className="screen-image screen-image--zoomable"
                                    onClick={() => handleZoomImage(currentScreenshot)}
                                    title="Click for Fullscreen"
                                />
                            ) : (
                                <div className="screen-placeholder">
                                    <div className="placeholder-content">
                                        <Sparkles size={32} />
                                        <p>Waiting for device stream...</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* AI Insight Bar */}
                    {aiInsight && !interventionRequest && (
                        <div className="ai-insight-bar">
                            <div className="insight-icon">
                                <Sparkles size={16} />
                            </div>
                            <div className="insight-content">
                                <span className="insight-label">AI INSIGHT</span>
                                <p className="insight-text">{aiInsight.reasoning}</p>
                            </div>
                        </div>
                    )}

                    {/* Human Intervention Header */}
                    <InterventionModal
                        request={interventionRequest}
                        onRespond={handleInterventionResponse}
                        open={!!interventionRequest}
                    />

                    {/* Playback Controls */}
                    <div className="playback-controls">
                        <button className="control-btn">
                            <SkipBack size={16} />
                        </button>
                        <button className="control-btn play">
                            <Play size={16} />
                        </button>
                        <button className="control-btn">
                            <SkipForward size={16} />
                        </button>
                        <div className="playback-time">{formatTime(elapsedTime)}</div>
                    </div>
                </div>

                {/* Right Panel - Live Logs */}
                <div className="logs-panel">
                    <div className="logs-header">
                        <h3>Live Logs</h3>
                        {/* <div className="logs-actions">
                            <button className="btn btn-ghost btn-sm"><Trash2 size={14} /> Clear</button>
                            <button className="btn btn-ghost btn-sm"><Download size={14} /> Export</button>
                        </div> */}
                    </div>

                    <div className="logs-filters">
                        {['All', 'AI', 'Info', 'Errors', 'Network'].map(filter => (
                            <button
                                key={filter}
                                className={`filter-btn ${activeLogFilter === filter ? 'active' : ''}`}
                                onClick={() => setActiveLogFilter(filter)}
                            >
                                {filter}
                            </button>
                        ))}
                    </div>

                    <div className="logs-container">
                        {filteredLogs.length > 0 ? (
                            filteredLogs.map((log, index) => (
                                <div key={index} className={`log-entry ${log.type.toLowerCase()}`}>
                                    <span className="log-time">[{log.timestamp}]</span>
                                    <span className="log-type">{log.type}</span>
                                    <span className="log-message">{log.message}</span>
                                </div>
                            ))
                        ) : (
                            <div className="logs-empty">
                                <p>No logs yet...</p>
                            </div>
                        )}
                        <div ref={logsEndRef} />
                    </div>

                    {/* <div className="logs-input">
                        <span className="input-prefix">&gt;</span>
                        <input
                            type="text"
                            placeholder="Type a command or filter logs..."
                            className="command-input"
                        />
                    </div> */}
                </div>
            </div>

            {/* Zoom Overlay */}
            {zoomedImage && (
                <div className="zoom-overlay" onClick={handleCloseZoom}>
                    <button className="zoom-close" onClick={handleCloseZoom}>
                        <X size={24} />
                    </button>
                    <div className="zoom-container" onClick={(e) => e.stopPropagation()}>
                        <img src={zoomedImage} alt="Zoomed screenshot" className="zoom-image" />
                    </div>
                </div>
            )}
        </div >
    );
}

export default LiveTestExecution;
